// Mobile Team Stats — a Basketball-Reference-style heatmap grid: teams as rows, a curated slice
// of the team box score as columns, each cell tinted by the team's league rank (green = good,
// red = poor). Reuses the `teamStats` worker data via the `teamStatsMobile` alias; the worker
// already hands us `allStats` (per-stat arrays sorted best-first, lower-is-better accounted for),
// so ranking a value is just its index. An Individual/Team toggle swaps back to player stats.

import { useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { useLocal } from "../util/local.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { helpers } from "../util/helpers.ts";
import { getCols } from "../../common/getCols.ts";
import type { View } from "../../common/types.ts";

const TYPES: [string, string][] = [
	["regularSeason", "Regular Season"],
	["playoffs", "Playoffs"],
];

// Curated, mobile-friendly slice of the team box score. Ranking direction (incl. TOV being
// lower-is-better) is already baked into the worker's `allStats` sort.
const COLS = [
	"pts",
	"fgp",
	"tpp",
	"ftp",
	"trb",
	"ast",
	"stl",
	"blk",
	"tov",
	"mov",
];

const GmTeamStats = ({
	teams,
	stats,
	allStats,
	averages,
	season,
	playoffs,
}: View<"teamStatsMobile">) => {
	useTitleBar({ title: "Team Stats" });
	const {
		lid,
		userTid,
		season: currentSeason,
		startingSeason,
	} = useLocal(["lid", "userTid", "season", "startingSeason"]);
	const [menu, setMenu] = useState<string | null>(null);

	// Anchor the year picker to the LEAGUE's current season (not the viewed one — anchoring to
	// the viewed season made past years shift the options and strand the user), spanning back to
	// startingSeason (1987 in leagues created with real historical stats).
	const seasonNum = typeof season === "number" ? season : currentSeason;
	const latestSeason = currentSeason ?? seasonNum;
	const earliestSeason = Math.min(startingSeason ?? latestSeason, latestSeason);
	const seasonOpts: number[] = [];
	for (let s = latestSeason; s >= earliestSeason; s--) {
		seasonOpts.push(s);
	}

	const cols = COLS.filter((s) => (stats as string[]).includes(s));
	const colDefs = getCols(cols.map((s) => `stat:${s}`));

	const goInd = () =>
		realtimeUpdate(
			[],
			`/l/${lid}/player_stats/all/${seasonNum}/perGame/${playoffs}`,
		);
	const goTeam = (s: number | string, po: string) => {
		setMenu(null);
		realtimeUpdate([], `/l/${lid}/team_stats_m/${s}/${po}`);
	};

	const rec = (t: any) => {
		const w = t.seasonAttrs.won || 0;
		const l = t.seasonAttrs.lost || 0;
		return w + l > 0 ? w / (w + l) : 0;
	};
	const rows = [...(teams as any[])].sort((a, b) => rec(b) - rec(a));

	// green (best) → red (worst), tinting stronger toward the extremes.
	const heat = (stat: string, value: number | undefined) => {
		const sorted = (allStats as any)[stat] as number[] | undefined;
		if (!sorted || sorted.length < 2 || value === undefined) return undefined;
		const idx = sorted.indexOf(value);
		if (idx < 0) return undefined;
		const pct = idx / (sorted.length - 1);
		const a = (Math.abs(pct - 0.5) * 2 * 0.5).toFixed(2);
		return pct <= 0.5 ? `rgba(24,224,168,${a})` : `rgba(226,80,78,${a})`;
	};

	const Pill = ({ id, label, options, onPick }: any) => (
		<div className="gts-pillwrap">
			<button
				className="gts-pill"
				onClick={() => setMenu(menu === id ? null : id)}
			>
				{label} ▾
			</button>
			{menu === id ? (
				<div className="gts-menu">
					{options.map((o: any) => (
						<button
							key={o.key}
							className="gts-menu-item"
							onClick={() => onPick(o.key)}
						>
							{o.active ? "✓ " : ""}
							{o.label}
						</button>
					))}
				</div>
			) : null}
		</div>
	);

	return (
		<div className="gts">
			<style>{styles}</style>
			<h1 className="gts-title">Team Stats</h1>

			<div className="gts-seg">
				<button className="gts-seg-btn" onClick={goInd}>
					Individual
				</button>
				<button className="gts-seg-btn active">Team</button>
			</div>

			<div className="gts-filters">
				<Pill
					id="season"
					label={`${seasonNum}`}
					options={seasonOpts.map((s) => ({
						key: s,
						label: `${s}`,
						active: s === seasonNum,
					}))}
					onPick={(s: number) => goTeam(s, playoffs)}
				/>
				<Pill
					id="type"
					label={TYPES.find(([k]) => k === playoffs)?.[1] ?? "Regular Season"}
					options={TYPES.map(([k, l]) => ({
						key: k,
						label: l,
						active: k === playoffs,
					}))}
					onPick={(po: string) => goTeam(seasonNum, po)}
				/>
			</div>

			<p className="gts-legend">
				<span className="gts-swatch gts-good" /> better ·{" "}
				<span className="gts-swatch gts-bad" /> worse — ranked across the league
			</p>

			<div className="gts-tablewrap">
				<table className="gts-table">
					<thead>
						<tr>
							<th className="gts-sticky gts-th">Team</th>
							<th className="gts-th">W-L</th>
							{cols.map((s, i) => (
								<th key={s} className="gts-th">
									{colDefs[i]?.title ?? s.toUpperCase()}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((t) => (
							<tr key={t.tid} className={t.tid === userTid ? "gts-me" : ""}>
								<td className="gts-sticky gts-team">{t.seasonAttrs.abbrev}</td>
								<td className="gts-rec">
									{t.seasonAttrs.won || 0}-{t.seasonAttrs.lost || 0}
								</td>
								{cols.map((s) => (
									<td key={s} style={{ background: heat(s, t.stats[s]) }}>
										{helpers.roundStat(t.stats[s], s)}
									</td>
								))}
							</tr>
						))}
						{averages ? (
							<tr className="gts-avg">
								<td className="gts-sticky gts-team">Lg</td>
								<td className="gts-rec">—</td>
								{cols.map((s) => (
									<td key={s}>
										{(averages as any)[s] !== undefined
											? helpers.roundStat((averages as any)[s], s)
											: "—"}
									</td>
								))}
							</tr>
						) : null}
					</tbody>
				</table>
			</div>
		</div>
	);
};

const styles = `
.gts { padding:16px 12px 40px; color:#fff; font-family:Montserrat,system-ui,sans-serif; }
.gts-title { font-weight:900; font-size:30px; margin:0 0 14px; letter-spacing:-.5px; padding:0 4px; }
.gts-seg { display:flex; gap:6px; background:#181818; border-radius:12px; padding:5px; margin:0 4px 12px; }
.gts-seg-btn { flex:1; border:none; background:none; color:#9fb0bc; font-weight:800; font-size:13px;
  padding:9px; border-radius:9px; cursor:pointer; font-family:inherit; }
.gts-seg-btn.active { background:#18E0A8; color:#0a0a0a; }
.gts-filters { display:flex; gap:8px; margin-bottom:10px; padding:0 4px; position:relative; z-index:5; }
.gts-pillwrap { position:relative; }
.gts-pill { border:none; border-radius:16px; background:#181818; color:#cfe0ec; font-weight:700;
  font-size:12px; padding:8px 13px; cursor:pointer; font-family:inherit; white-space:nowrap; }
.gts-menu { position:absolute; top:100%; left:0; margin-top:6px; background:#1c1c1c; border:1px solid #2a2a2a;
  border-radius:10px; padding:5px; min-width:150px; max-height:280px; overflow-y:auto;
  box-shadow:0 12px 30px rgba(0,0,0,.5); z-index:10; }
.gts-menu-item { display:block; width:100%; text-align:left; background:none; border:none; color:#cfe0ec;
  font-size:13px; font-weight:600; padding:9px 10px; border-radius:7px; cursor:pointer; font-family:inherit; white-space:nowrap; }
.gts-menu-item:hover { background:#232323; }
.gts-legend { display:flex; align-items:center; gap:6px; font-size:11px; color:#8a8a8a; padding:0 4px; margin:0 0 10px; }
.gts-swatch { display:inline-block; width:12px; height:12px; border-radius:3px; }
.gts-good { background:rgba(24,224,168,.5); }
.gts-bad { background:rgba(226,80,78,.5); }
.gts-tablewrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
.gts-table { border-collapse:collapse; font-size:12px; white-space:nowrap; }
.gts-th { color:#8fb3c9; font-weight:800; font-size:10px; letter-spacing:.4px; padding:8px 9px;
  text-align:right; border-bottom:1px solid #232323; }
.gts-table td { padding:9px; text-align:right; border-bottom:1px solid #181818; color:#e6edf2; }
.gts-sticky { position:sticky; left:0; text-align:left !important; background:#121212; z-index:2; }
.gts-th.gts-sticky { z-index:3; }
.gts-team { font-weight:800; font-size:13px; }
.gts-rec { color:#8fb3c9; }
.gts-me .gts-sticky { background:#13251f; }
.gts-me .gts-team { color:#18E0A8; }
.gts-avg td { color:#8a8a8a; border-top:1px solid #2a2a2a; }
`;

export default GmTeamStats;
